import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
  Typography, CircularProgress, Alert, TextField, Select, MenuItem,
  FormControl, InputLabel, Grid, Chip, LinearProgress, Divider,
  List, ListItem, ListItemIcon, ListItemText, FormControlLabel, Switch,
  Stack, InputAdornment, Tooltip
} from '@mui/material';
import {
  AutoAwesome, CloudUpload, CheckCircle, SkipNext, Description,
  NavigateNext, Add as AddIcon, VisibilityOff as HiddenIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { addMonths, format as fnsFormat, parseISO, isValid } from 'date-fns';
import { importsApi, adminApi } from '../../services/api';
import {
  FIELD_SECTIONS, ALL_FIELD_KEYS, ALWAYS_VISIBLE,
  isFieldVisible, buildDefaultConfig, type VisibilityConfig
} from '../../config/fieldVisibilityConfig';

// ----------------------------------------------------------------
// Constants
// ----------------------------------------------------------------
const CONTRACT_TYPES = ['MSA', 'SOW', 'SLA', 'NDA', 'IRU', 'VENDOR', 'CUSTOMER', 'OTHER'];
const COUNTERPARTY_TYPES = ['Vendor', 'Customer', 'Partner', 'Contractor', 'Employee', 'Government', 'Other'];
const STATUSES = ['draft', 'active', 'suspended'];
const CURRENCIES = ['USD', 'GBP', 'EUR', 'AUD', 'CAD', 'NZD'];
const BILLING_FREQS = ['monthly', 'quarterly', 'annually', 'one-time'];
const PAYMENT_TERMS_OPTIONS = ['Due on Receipt', 'Net 15', 'Net 30', 'Net 45', 'Net 60', 'Net 90'];

const DISPUTE_METHODS = ['Litigation', 'Arbitration', 'Mediation', 'Mediation then Arbitration', 'Negotiation then Litigation', 'Other'];
const DATA_CLASSIFICATIONS = ['None', 'Confidential', 'PII', 'PHI', 'Proprietary', 'Trade Secret'];

// Fields rendered as dropdowns
const SELECT_FIELDS: Record<string, string[]> = {
  contract_type: CONTRACT_TYPES,
  status: STATUSES,
  counterparty_type: COUNTERPARTY_TYPES,
  currency: CURRENCIES,
  billing_frequency: BILLING_FREQS,
  payment_terms: PAYMENT_TERMS_OPTIONS,
  dispute_resolution_method: DISPUTE_METHODS,
  data_classification: DATA_CLASSIFICATIONS,
};

// Fields rendered as date inputs
const DATE_FIELDS = new Set([
  'effective_date', 'start_date', 'expiration_date', 'end_date',
  'counterparty_signature_date', 'hyperlink_signature_date'
]);

// Fields rendered as number inputs
const NUMBER_FIELDS = new Set([
  'contract_value', 'term_length_months', 'notice_period_days',
  'renewal_term_months', 'payment_terms_days', 'late_fees_pct',
  'termination_for_convenience_days', 'termination_for_cause_days',
  'mrc', 'nrc', 'liability_cap',
  'confidentiality_period_months', 'non_compete_months', 'non_solicit_months',
  'fiber_strand_count', 'bandwidth_gbps',
  'sla_uptime_pct', 'sla_response_time_hours', 'sla_credit_pct'
]);

// Fields rendered as multiline
const MULTILINE_FIELDS = new Set([
  'description', 'scope_summary', 'address', 'renewal_terms', 'renewal_notes', 'assignment_rights',
  'amendment_requirements', 'billing_milestones', 'credits_penalties_refunds', 'taxes_pass_through',
  'indemnification_scope', 'insurance_requirements', 'liability_cap_multiplier',
  'fiber_route'
]);

// Fields rendered as boolean switch
const BOOLEAN_FIELDS = new Set(['auto_renews', 'force_majeure', 'parent_guarantee']);

// Fields rendered as chip/tag inputs
const CHIP_FIELDS = new Set(['key_terms', 'tags']);

// ----------------------------------------------------------------
// Types
// ----------------------------------------------------------------
type FileStatus = 'pending' | 'extracting' | 'reviewing' | 'importing' | 'imported' | 'skipped' | 'error';

interface FileEntry {
  file: File;
  status: FileStatus;
  extracted?: Record<string, any>;
  error?: string;
  contractName?: string;
}

type FormValues = Record<string, string | string[]>;

function buildEmptyForm(): FormValues {
  const form: FormValues = {};
  ALL_FIELD_KEYS.forEach(k => { form[k] = ''; });
  form.status = 'draft';
  form.currency = 'USD';
  form.notice_period_days = '30';
  return form;
}

/** Calculates the end date from an effective date + term months. Returns null if inputs are invalid. */
function calcEndDate(effectiveDateStr: string, termMonthsStr: string): string | null {
  try {
    if (!effectiveDateStr || !termMonthsStr) return null;
    const termMonths = parseFloat(termMonthsStr);
    if (isNaN(termMonths) || termMonths <= 0) return null;
    const base = parseISO(effectiveDateStr);
    if (!isValid(base)) return null;
    return fnsFormat(addMonths(base, termMonths), 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

function extractedToForm(
  extracted: Record<string, any>,
  filename: string
): { form: FormValues; endDateCalc: string | null } {
  const form = buildEmptyForm();
  const str = (v: any) => (v !== null && v !== undefined ? String(v) : '');
  for (const key of ALL_FIELD_KEYS) {
    if (extracted[key] !== null && extracted[key] !== undefined) {
      if (CHIP_FIELDS.has(key)) {
        // key_terms arrives as an array from Claude
        if (Array.isArray(extracted[key])) {
          form[key] = extracted[key].map(String);
        } else if (typeof extracted[key] === 'string') {
          try { const parsed = JSON.parse(extracted[key]); form[key] = Array.isArray(parsed) ? parsed.map(String) : []; }
          catch { form[key] = []; }
        }
      } else if (typeof extracted[key] === 'boolean') {
        form[key] = extracted[key] ? '1' : '0';
      } else {
        form[key] = str(extracted[key]);
      }
    }
  }
  if (!form.contract_name) form.contract_name = filename.replace(/\.[^.]+$/, '');
  if (!form.currency) form.currency = 'USD';

  // Status: Claude may return it directly, or infer from signatures
  if (!form.status || form.status === '') {
    if (extracted.counterparty_signature_date || extracted.hyperlink_signature_date) {
      form.status = 'active';
    } else {
      form.status = 'draft';
    }
  }

  // Auto-calculate end_date from effective_date + term_length_months
  const endDateCalc = calcEndDate(form.effective_date as string, form.term_length_months as string);
  if (endDateCalc && (!form.end_date || form.end_date === '')) {
    // End date wasn't extracted — fill it automatically
    form.end_date = endDateCalc;
  }

  return { form, endDateCalc };
}

// ----------------------------------------------------------------
// Sub-component: File selection drop zone
// ----------------------------------------------------------------
function FileSelector({ onFilesSelected }: { onFilesSelected: (files: File[]) => void }) {
  const [queued, setQueued] = useState<File[]>([]);

  const onDrop = useCallback((accepted: File[]) => {
    setQueued(prev => {
      const names = new Set(prev.map(f => f.name));
      return [...prev, ...accepted.filter(f => !names.has(f.name))];
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
    },
    multiple: true
  });

  const remove = (name: string) => setQueued(prev => prev.filter(f => f.name !== name));

  return (
    <Box>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.default',
          transition: 'all 0.2s'
        }}
      >
        <input {...getInputProps()} />
        <CloudUpload sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="body1" fontWeight={500}>
          {isDragActive ? 'Drop files here' : 'Drag & drop contract files here'}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          PDF and Word documents (.pdf, .doc, .docx) — multiple files supported
        </Typography>
        <Button variant="outlined" size="small" sx={{ mt: 2 }}>Browse Files</Button>
      </Box>

      {queued.length > 0 && (
        <Box mt={2}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
            {queued.length} file{queued.length !== 1 ? 's' : ''} selected
          </Typography>
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {queued.map(f => (
              <ListItem
                key={f.name}
                secondaryAction={
                  <Button size="small" color="error" onClick={() => remove(f.name)}>Remove</Button>
                }
                sx={{ px: 0 }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Description fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={f.name}
                  secondary={`${(f.size / 1024).toFixed(0)} KB`}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            startIcon={<AutoAwesome />}
            onClick={() => onFilesSelected(queued)}
          >
            Start Import ({queued.length} file{queued.length !== 1 ? 's' : ''})
          </Button>
        </Box>
      )}
    </Box>
  );
}

// ----------------------------------------------------------------
// ChipInput — chip-tag input for key_terms and tags
// ----------------------------------------------------------------
function ChipInput({
  label, value, onChange, extracted
}: {
  label: string;
  value: string[];
  onChange: (val: string[]) => void;
  extracted: boolean;
}) {
  const [input, setInput] = useState('');

  const add = () => {
    const trimmed = input.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
    }
    setInput('');
  };

  const remove = (chip: string) => onChange(value.filter(v => v !== chip));

  return (
    <Box>
      <Typography variant="caption" color={extracted ? 'primary.main' : 'text.secondary'} fontWeight={600}>
        {label}
        {extracted && <Chip label="AI" size="small" color="primary" variant="outlined" sx={{ ml: 0.5, height: 16, fontSize: 10 }} />}
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5} mb={0.5} minHeight={32}>
        {value.map(chip => (
          <Chip key={chip} label={chip} size="small" onDelete={() => remove(chip)} />
        ))}
      </Stack>
      <TextField
        size="small"
        fullWidth
        placeholder={`Add ${label.toLowerCase()} and press Enter`}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Button size="small" onClick={add} disabled={!input.trim()} sx={{ minWidth: 0, p: 0.5 }}>
                <AddIcon fontSize="small" />
              </Button>
            </InputAdornment>
          )
        }}
      />
    </Box>
  );
}

// ----------------------------------------------------------------
// Field renderer — renders the right input type for each field
// dimmed=true means the field is hidden for this contract type per the
// admin matrix — it is shown greyed-out and disabled until the user
// manually changes the contract type to one that includes it.
// ----------------------------------------------------------------
function FieldInput({
  fieldKey, label, value, onChange, extracted, dimmed, warning
}: {
  fieldKey: string; label: string; value: string | string[];
  onChange: (key: string, val: string | string[]) => void;
  extracted: boolean;
  dimmed: boolean; // not applicable for current contract type per admin matrix
  warning?: string; // shown as helper text in amber when present
}) {
  const strVal = Array.isArray(value) ? '' : value;
  const arrVal = Array.isArray(value) ? value : [];
  const isEmpty = !strVal || strVal === '0';
  const errorStyle = !dimmed && !extracted && isEmpty && !CHIP_FIELDS.has(fieldKey) && !BOOLEAN_FIELDS.has(fieldKey);

  // Shared dimmed overlay wrapper
  const wrapDimmed = (el: React.ReactNode) => (
    <Tooltip
      title={dimmed ? 'Not used for this contract type — change the Contract Type above to enable' : ''}
      placement="top"
      disableHoverListener={!dimmed}
    >
      <Box sx={{ position: 'relative', opacity: dimmed ? 0.38 : 1, pointerEvents: dimmed ? 'none' : 'auto' }}>
        {el}
        {dimmed && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'flex-end', pr: 1, pointerEvents: 'none'
          }}>
            <HiddenIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          </Box>
        )}
      </Box>
    </Tooltip>
  );

  if (CHIP_FIELDS.has(fieldKey)) {
    return wrapDimmed(
      <ChipInput
        label={label}
        value={arrVal}
        onChange={val => onChange(fieldKey, val)}
        extracted={extracted}
      />
    );
  }

  if (BOOLEAN_FIELDS.has(fieldKey)) {
    return wrapDimmed(
      <FormControlLabel
        control={
          <Switch
            checked={strVal === '1' || strVal === 'true'}
            onChange={e => onChange(fieldKey, e.target.checked ? '1' : '0')}
            size="small"
            disabled={dimmed}
          />
        }
        label={<Typography variant="body2" color={extracted ? 'primary.main' : 'text.primary'}>{label}</Typography>}
      />
    );
  }

  if (SELECT_FIELDS[fieldKey]) {
    return wrapDimmed(
      <FormControl fullWidth size="small" error={errorStyle}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={strVal}
          label={label}
          disabled={dimmed}
          onChange={e => onChange(fieldKey, e.target.value as string)}
        >
          <MenuItem value="">-</MenuItem>
          {SELECT_FIELDS[fieldKey].map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  return wrapDimmed(
    <TextField
      label={label}
      fullWidth
      size="small"
      disabled={dimmed}
      type={DATE_FIELDS.has(fieldKey) ? 'date' : NUMBER_FIELDS.has(fieldKey) ? 'number' : 'text'}
      multiline={MULTILINE_FIELDS.has(fieldKey)}
      rows={MULTILINE_FIELDS.has(fieldKey) ? 2 : undefined}
      value={strVal}
      onChange={e => onChange(fieldKey, e.target.value)}
      InputLabelProps={DATE_FIELDS.has(fieldKey) ? { shrink: true } : undefined}
      error={errorStyle}
      helperText={warning ?? undefined}
      FormHelperTextProps={warning ? { sx: { color: 'warning.main', fontWeight: 500 } } : undefined}
      sx={errorStyle ? { '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: 'error.main' } } } : undefined}
    />
  );
}

// ----------------------------------------------------------------
// Main dialog
// ----------------------------------------------------------------
interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

export default function ImportContractsDialog({ open, onClose, onImported }: Props) {
  const [phase, setPhase] = useState<'selecting' | 'reviewing' | 'done'>('selecting');
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [form, setForm] = useState<FormValues>(buildEmptyForm());
  const [importError, setImportError] = useState<string | null>(null);
  const [endDateCalc, setEndDateCalc] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);

  // File-level duplicate (same SHA-256 already in the system)
  // - hardBlocked: viewer/editor cannot proceed
  // - canOverride: manager/admin can click "Import anyway"
  type DocumentDuplicate = {
    document_id: string;
    contract_id: string;
    contract_name: string | null;
    document_name: string;
    uploader_name: string | null;
    uploaded_at: string;
  };
  const [docDuplicate, setDocDuplicate] = useState<DocumentDuplicate | null>(null);
  const [docDuplicateBlocked, setDocDuplicateBlocked] = useState(false);
  const [docDuplicateMessage, setDocDuplicateMessage] = useState<string | null>(null);

  // Admin field-visibility matrix — loaded once when dialog opens
  const [visibilityConfig, setVisibilityConfig] = useState<VisibilityConfig>(buildDefaultConfig());
  useEffect(() => {
    if (!open) return;
    adminApi.getPublicSetting('field_visibility')
      .then(res => {
        try {
          const parsed = JSON.parse(res.data.value || '{}');
          // If the stored config is non-empty, use it; otherwise fall back to defaults
          setVisibilityConfig(Object.keys(parsed).length > 0 ? parsed : buildDefaultConfig());
        } catch {
          setVisibilityConfig(buildDefaultConfig());
        }
      })
      .catch(() => setVisibilityConfig(buildDefaultConfig()));
  }, [open]);

  /** True if field should be dimmed for the current contract type */
  const isDimmed = useCallback((fieldKey: string) => {
    const contractType = String(form.contract_type || '');
    if (!contractType || ALWAYS_VISIBLE.has(fieldKey)) return false;
    return !isFieldVisible(visibilityConfig, contractType, fieldKey);
  }, [visibilityConfig, form.contract_type]);
  const [extractedKeys, setExtractedKeys] = useState<Set<string>>(new Set());

  function handleClose() {
    if (phase === 'reviewing' && entries.some(e => e.status === 'extracting' || e.status === 'importing')) return;
    setPhase('selecting');
    setEntries([]);
    setCurrentIdx(0);
    setEndDateCalc(null);
    onClose();
  }

  async function startImport(files: File[]) {
    const initial: FileEntry[] = files.map(f => ({ file: f, status: 'pending' }));
    setEntries(initial);
    setCurrentIdx(0);
    setPhase('reviewing');
    await runPreview(0, initial);
  }

  async function runPreview(idx: number, entriesList: FileEntry[]) {
    const entry = entriesList[idx];
    if (!entry) return;

    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, status: 'extracting' } : e));
    setDocDuplicate(null);
    setDocDuplicateBlocked(false);
    setDocDuplicateMessage(null);

    const formData = new FormData();
    formData.append('file', entry.file);

    try {
      const res = await importsApi.preview(formData);
      const { extracted, filename, has_text, message, duplicate } = res.data;
      const { form: newForm, endDateCalc: calc } = extractedToForm(extracted, filename);

      // Manager+ saw a duplicate at preview time — show the warning but
      // allow them to proceed with review and override at confirm.
      if (duplicate) {
        setDocDuplicate(duplicate);
        setDocDuplicateMessage('This file has already been uploaded. You can review and import anyway.');
      }

      // Track which fields Claude actually populated
      const populated = new Set<string>();
      for (const key of ALL_FIELD_KEYS) {
        if (extracted[key] !== null && extracted[key] !== undefined && extracted[key] !== '') {
          populated.add(key);
        }
      }
      setExtractedKeys(populated);

      if (!has_text) {
        setEntries(prev => prev.map((e, i) => i === idx
          ? { ...e, status: 'error', error: message || 'Could not extract text from this document. You can fill in the fields manually.' }
          : e
        ));
        setForm({ ...buildEmptyForm(), contract_name: entry.file.name.replace(/\.[^.]+$/, '') });
        setExtractedKeys(new Set());
        setEndDateCalc(null);
        setCurrentIdx(idx);
        return;
      }

      setEntries(prev => prev.map((e, i) => i === idx
        ? { ...e, status: 'reviewing', extracted, contractName: String(newForm.contract_name || '') }
        : e
      ));
      setForm(newForm);
      setEndDateCalc(calc);
      setCurrentIdx(idx);
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;

      // Hard-blocked duplicate (viewer/editor) — show message; do not allow proceed.
      if (status === 403 && data?.error === 'duplicate_document') {
        setDocDuplicate(data.duplicate);
        setDocDuplicateBlocked(true);
        setDocDuplicateMessage(data.message || 'This file has already been uploaded.');
        setEntries(prev => prev.map((e, i) => i === idx
          ? { ...e, status: 'error', error: 'Duplicate file — see manager' }
          : e
        ));
        setForm({ ...buildEmptyForm(), contract_name: entry.file.name.replace(/\.[^.]+$/, '') });
        setExtractedKeys(new Set());
        setEndDateCalc(null);
        setCurrentIdx(idx);
        return;
      }

      const msg = err.code === 'ECONNABORTED'
        ? 'Request timed out — the document may be too large or a scanned image. You can fill in the fields manually.'
        : (data?.error || 'Extraction failed');
      setEntries(prev => prev.map((e, i) => i === idx ? { ...e, status: 'error', error: msg } : e));
      setForm({ ...buildEmptyForm(), contract_name: entry.file.name.replace(/\.[^.]+$/, '') });
      setExtractedKeys(new Set());
      setEndDateCalc(null);
      setCurrentIdx(idx);
    }
  }

  async function handleImport(force = false) {
    const entry = entries[currentIdx];
    setImportError(null);
    setDuplicateWarning(null);
    setEntries(prev => prev.map((e, i) => i === currentIdx ? { ...e, status: 'importing' } : e));

    const formData = new FormData();
    formData.append('file', entry.file);

    // Merge form values with remaining extracted fields
    const allFields: Record<string, any> = { ...(entry.extracted || {}), ...form };

    // Convert boolean switch values to actual booleans
    for (const boolField of ['auto_renews', 'force_majeure', 'parent_guarantee']) {
      if (allFields[boolField] !== undefined) {
        allFields[boolField] = allFields[boolField] === '1' || allFields[boolField] === 'true' || allFields[boolField] === true;
      }
    }

    // Chip fields: keep as arrays (backend will JSON.stringify them)
    for (const chipField of ['key_terms', 'tags']) {
      if (Array.isArray(allFields[chipField]) && allFields[chipField].length === 0) {
        allFields[chipField] = null;
      }
    }

    formData.append('fields', JSON.stringify(allFields));

    try {
      await importsApi.confirm(formData, force);
      setEntries(prev => prev.map((e, i) => i === currentIdx
        ? { ...e, status: 'imported', contractName: String(form.contract_name || '') }
        : e
      ));
      onImported();
      advanceToNext(currentIdx + 1);
    } catch (err: any) {
      const status = err.response?.status;
      const data = err.response?.data;
      if (status === 409 && data?.error === 'duplicate') {
        // Contract NAME duplicate — keep in reviewing state, allow Import Anyway
        setDuplicateWarning(data.message);
        setEntries(prev => prev.map((e, i) => i === currentIdx ? { ...e, status: 'reviewing' } : e));
      } else if (status === 409 && data?.error === 'duplicate_document') {
        // FILE duplicate, manager+ can override
        setDocDuplicate(data.duplicate);
        setDocDuplicateBlocked(false);
        setDocDuplicateMessage(data.message || 'This file has already been uploaded. You can override.');
        setEntries(prev => prev.map((e, i) => i === currentIdx ? { ...e, status: 'reviewing' } : e));
      } else if (status === 403 && data?.error === 'duplicate_document') {
        // FILE duplicate, viewer/editor cannot override
        setDocDuplicate(data.duplicate);
        setDocDuplicateBlocked(true);
        setDocDuplicateMessage(data.message || 'This file has already been uploaded.');
        setEntries(prev => prev.map((e, i) => i === currentIdx ? { ...e, status: 'reviewing' } : e));
      } else {
        const msg = data?.error || 'Import failed';
        setImportError(msg);
        setEntries(prev => prev.map((e, i) => i === currentIdx ? { ...e, status: 'reviewing' } : e));
      }
    }
  }

  function handleSkip() {
    setEntries(prev => prev.map((e, i) => i === currentIdx ? { ...e, status: 'skipped' } : e));
    advanceToNext(currentIdx + 1);
  }

  function advanceToNext(nextIdx: number) {
    if (nextIdx >= entries.length) {
      setPhase('done');
      return;
    }
    const next = entries[nextIdx];
    if (next.status !== 'pending') {
      advanceToNext(nextIdx + 1);
      return;
    }
    runPreview(nextIdx, entries);
  }

  function handleFieldChange(key: string, value: string | string[]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  // Recompute the expected end date whenever effective_date or term_length_months changes
  useEffect(() => {
    const calc = calcEndDate(form.effective_date as string, form.term_length_months as string);
    setEndDateCalc(calc);
  }, [form.effective_date, form.term_length_months]);

  // Warning shown on the End Date field when the value doesn't match the auto-calculation
  const endDateWarning = useMemo<string | undefined>(() => {
    const currentEnd = form.end_date as string;
    if (!endDateCalc || !currentEnd) return undefined;
    if (currentEnd !== endDateCalc) {
      return `Expected ${endDateCalc} based on effective date + ${form.term_length_months} months`;
    }
    return undefined;
  }, [endDateCalc, form.end_date, form.term_length_months]);

  // Warning shown on the Effective Date field when the value was derived
  // from signature dates (vs. an explicit clause). Helps users understand
  // and audit the auto-fill.
  const effectiveDateWarning = useMemo<string | undefined>(() => {
    const basis = entries[currentIdx]?.extracted?.effective_date_basis as string | undefined;
    const cp = entries[currentIdx]?.extracted?.counterparty_signature_date;
    const hl = entries[currentIdx]?.extracted?.hyperlink_signature_date;
    if (!basis || basis === 'explicit_clause') return undefined;
    if (basis === 'last_signature') {
      return 'No explicit effective date in contract — used the later signature date';
    }
    if (basis === 'single_signature') {
      const which = cp && !hl ? 'counterparty' : (!cp && hl ? 'HyperLink' : 'the dated');
      return `Only ${which} signature is dated — used that date as effective date`;
    }
    if (basis === 'unknown') {
      return 'No explicit effective date and no signature dates — please fill manually';
    }
    return undefined;
  }, [entries, currentIdx]);

  // ── Render ──────────────────────────────────────────────────────
  const current = entries[currentIdx];
  const importedCount = entries.filter(e => e.status === 'imported').length;
  const skippedCount = entries.filter(e => e.status === 'skipped').length;
  const populatedCount = extractedKeys.size;
  const totalFields = ALL_FIELD_KEYS.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AutoAwesome color="primary" />
          <Typography variant="h6">Import Contracts</Typography>
        </Box>
        {phase === 'reviewing' && (
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            File {currentIdx + 1} of {entries.length}: <strong>{current?.file.name}</strong>
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>

        {/* ── Phase: Selecting ── */}
        {phase === 'selecting' && (
          <FileSelector onFilesSelected={startImport} />
        )}

        {/* ── Phase: Reviewing ── */}
        {phase === 'reviewing' && current && (
          <Box>
            {/* Progress bar */}
            <LinearProgress
              variant="determinate"
              value={((currentIdx) / entries.length) * 100}
              sx={{ mb: 2, borderRadius: 1 }}
            />

            {/* Extracting state */}
            {current.status === 'extracting' && (
              <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={2}>
                <CircularProgress size={44} />
                <Typography color="text.secondary">
                  Analysing with Claude Opus&hellip;
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Extracting contract fields from {current.file.name}
                </Typography>
              </Box>
            )}

            {/* Importing state */}
            {current.status === 'importing' && (
              <Box display="flex" flexDirection="column" alignItems="center" py={5} gap={2}>
                <CircularProgress size={44} />
                <Typography color="text.secondary">Creating contract record…</Typography>
              </Box>
            )}

            {/* Error state — still allow editing */}
            {current.status === 'error' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {current.error} — You can still fill in the fields manually and import.
              </Alert>
            )}

            {duplicateWarning && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => { void handleImport(true); }}>
                    Import Anyway
                  </Button>
                }
              >
                <strong>Possible duplicate:</strong> {duplicateWarning}
              </Alert>
            )}

            {docDuplicate && (
              <Alert
                severity={docDuplicateBlocked ? 'error' : 'warning'}
                sx={{ mb: 2 }}
                action={
                  !docDuplicateBlocked && (
                    <Button color="inherit" size="small" onClick={() => { void handleImport(true); }}>
                      Import Anyway
                    </Button>
                  )
                }
              >
                <strong>{docDuplicateBlocked ? 'This file has already been uploaded.' : 'Possible duplicate file:'}</strong>{' '}
                {docDuplicateMessage}
                <Box mt={0.5} sx={{ fontSize: 13 }}>
                  Originally uploaded as <strong>{docDuplicate.document_name}</strong>
                  {docDuplicate.contract_name && <> on contract <strong>{docDuplicate.contract_name}</strong></>}
                  {docDuplicate.uploader_name && <> by {docDuplicate.uploader_name}</>}
                  {docDuplicate.uploaded_at && <> on {String(docDuplicate.uploaded_at).slice(0, 10)}</>}.
                  {docDuplicate.contract_id && (
                    <> <Button
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 0, textTransform: 'none' }}
                      onClick={() => window.open(`/contracts/${docDuplicate.contract_id}`, '_blank')}
                    >
                      View contract →
                    </Button></>
                  )}
                </Box>
              </Alert>
            )}

            {importError && (
              <Alert severity="error" sx={{ mb: 2 }}>{importError}</Alert>
            )}

            {/* Review form — all fields */}
            {(current.status === 'reviewing' || current.status === 'error') && (
              <Box>
                {/* Extraction stats */}
                <Box display="flex" gap={1} mb={2}>
                  <Chip
                    label={`${populatedCount} of ${totalFields} fields extracted`}
                    size="small"
                    color={populatedCount > 0 ? 'primary' : 'default'}
                    variant="outlined"
                  />
                  {totalFields - populatedCount > 0 && (
                    <Chip
                      label={`${totalFields - populatedCount} not identified`}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  )}
                </Box>

                {/* Dynamic form from FIELD_SECTIONS */}
                {FIELD_SECTIONS.map((section, si) => {
                  // Count how many fields in this section are dimmed vs visible
                  const sectionDimmedCount = section.fields.filter(f => isDimmed(f.key)).length;
                  const allSectionDimmed = sectionDimmedCount === section.fields.length;

                  // Show a party banner when the section switches to a named party
                  const prevParty = si > 0 ? FIELD_SECTIONS[si - 1].party : undefined;
                  const showPartyBanner = section.party !== undefined && section.party !== prevParty;

                  return (
                  <Box key={section.section}>
                    {/* Party transition banner */}
                    {showPartyBanner && (
                      <Box sx={{
                        mt: si > 0 ? 2 : 0, mb: 1,
                        px: 1.5, py: 0.75,
                        borderRadius: 1,
                        bgcolor: section.party === 'hyperlink' ? 'primary.main' : 'grey.100',
                        display: 'flex', alignItems: 'center', gap: 1,
                      }}>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          textTransform="uppercase"
                          letterSpacing={0.8}
                          color={section.party === 'hyperlink' ? 'primary.contrastText' : 'text.secondary'}
                        >
                          {section.party === 'hyperlink' ? 'HyperLink Infrastructure — Our Information' : 'Counterparty — Other Party'}
                        </Typography>
                      </Box>
                    )}

                    {/* Regular divider between sections of the same party */}
                    {si > 0 && !showPartyBanner && <Divider sx={{ my: 1.5 }} />}

                    <Box display="flex" alignItems="center" gap={1} mt={showPartyBanner ? 1 : 0}>
                      <Typography variant="caption" fontWeight={600} color={allSectionDimmed ? 'text.disabled' : 'text.secondary'} textTransform="uppercase">
                        {section.section}
                      </Typography>
                      {allSectionDimmed && (
                        <Chip
                          label="Not used for this type"
                          size="small"
                          icon={<HiddenIcon sx={{ fontSize: '12px !important' }} />}
                          sx={{ height: 18, fontSize: 10, color: 'text.disabled', borderColor: 'divider' }}
                          variant="outlined"
                        />
                      )}
                      {!allSectionDimmed && sectionDimmedCount > 0 && (
                        <Chip
                          label={`${sectionDimmedCount} field${sectionDimmedCount > 1 ? 's' : ''} not used`}
                          size="small"
                          sx={{ height: 18, fontSize: 10, color: 'text.disabled', borderColor: 'divider' }}
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Grid container spacing={1.5} mt={0.25} mb={1}>
                      {section.fields.map(field => {
                        // Choose grid width based on field type
                        const isWide = MULTILINE_FIELDS.has(field.key) || field.key === 'contract_name';
                        const isMedium = DATE_FIELDS.has(field.key) || NUMBER_FIELDS.has(field.key) || BOOLEAN_FIELDS.has(field.key);
                        const sm = isWide ? 12 : isMedium ? 4 : 6;
                        const dimmed = isDimmed(field.key);

                        return (
                          <Grid item xs={12} sm={sm} key={field.key}>
                            <FieldInput
                              fieldKey={field.key}
                              label={field.label}
                              value={form[field.key] || ''}
                              onChange={handleFieldChange}
                              extracted={extractedKeys.has(field.key)}
                              dimmed={dimmed}
                              warning={
                                field.key === 'end_date' ? endDateWarning :
                                field.key === 'effective_date' ? effectiveDateWarning :
                                undefined
                              }
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ── Phase: Done ── */}
        {phase === 'done' && (
          <Box>
            <Box display="flex" alignItems="center" gap={1.5} mb={3}>
              <CheckCircle color="success" sx={{ fontSize: 36 }} />
              <Box>
                <Typography variant="h6">Import Complete</Typography>
                <Typography variant="body2" color="text.secondary">
                  {importedCount} imported · {skippedCount} skipped
                </Typography>
              </Box>
            </Box>
            <List dense>
              {entries.map((e, i) => (
                <ListItem key={i} sx={{ px: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {e.status === 'imported'
                      ? <CheckCircle color="success" fontSize="small" />
                      : <SkipNext color="disabled" fontSize="small" />}
                  </ListItemIcon>
                  <ListItemText
                    primary={e.contractName || e.file.name}
                    secondary={e.status === 'imported' ? 'Imported' : 'Skipped'}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: e.status === 'imported' ? 'success.main' : 'text.disabled'
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'selecting' && (
          <Button onClick={handleClose}>Cancel</Button>
        )}

        {phase === 'reviewing' && (current?.status === 'reviewing' || current?.status === 'error') && (
          <>
            <Button onClick={handleClose} color="inherit">Cancel</Button>
            <Box flex={1} />
            <Button
              startIcon={<SkipNext />}
              onClick={handleSkip}
              color="inherit"
            >
              Skip
            </Button>
            <Button
              variant="contained"
              startIcon={<NavigateNext />}
              onClick={() => { void handleImport(); }}
              disabled={!String(form.contract_name || '').trim() || docDuplicateBlocked}
            >
              {currentIdx === entries.length - 1 ? 'Import & Finish' : 'Import & Next'}
            </Button>
          </>
        )}

        {phase === 'done' && (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
